import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Footer } from "../components/Footer";
import { LanguageToggle } from "../components/LanguageToggle";
import { useReactiveLang } from "../lib/i18n";

const CONTACT = "hello@islandsdaily.com";

const LANGS = [
  { code: "en", label: "English" },
  { code: "nl", label: "Nederlands" },
  { code: "es", label: "Español" },
];

const COPY = {
  en: {
    sections: [
      {
        h: "Our Story",
        paras: [
          "IslandsDaily is the Caribbean Morning Briefing — the only platform bringing together news from Aruba, Curaçao, Bonaire, and St. Maarten in English, Dutch, and Spanish, updated every 15 minutes.",
          "Local newspapers in Aruba, Curaçao, Bonaire, and St. Maarten publish hundreds of stories every week in Papiamento, Dutch, and Papiamentu. Tourists arrive on these islands and have no idea what is happening around them. Expats and long-term residents piece together information from multiple sources in languages they may not fully understand. Even English-speaking locals find it difficult to follow news across all four islands in one place.",
          "IslandsDaily was created to solve that. We bring together headlines from the leading local news sources across all four islands, translate and summarize them into clear English using AI, and present everything in one clean, easy-to-read platform — updated every 15 minutes.",
        ],
      },
      {
        h: "What We Do",
        paras: [
          "Every 15 minutes, IslandsDaily automatically collects the latest headlines from trusted local news sources across Aruba, Curaçao, Bonaire, and St. Maarten. Our AI editorial system reads each article — in whatever language it was originally written — and produces a clear, accurate, 5-7 sentence English summary covering who, what, where, when, and why it matters.",
          "We never reproduce full articles. We always credit the original source. And we always give readers a direct link to read the full story on the publisher's own website in its original language — modern browsers offer one-tap translation if the reader wants it.",
        ],
      },
      {
        h: "Our Mission",
        paras: [
          "To make the Dutch Caribbean's stories accessible to everyone — regardless of what language they speak.",
          "We believe local news matters. We believe the stories happening in Oranjestad, Willemstad, Kralendijk, and Philipsburg deserve to be heard beyond the islands. And we believe that language should never be a barrier to staying informed about the place you live in, work in, or are visiting.",
        ],
      },
      {
        h: "Our Relationship With Local Publishers",
        paras: [
          "We have deep respect for the journalists and publishers who do the hard work of reporting local Caribbean news every day. IslandsDaily exists because of their work — not in spite of it.",
          "Every article we feature credits the original publisher and links directly back to their website. We drive traffic to local news sources, not away from them. Our goal is to expand the audience for Caribbean journalism — bringing English-speaking readers to local stories they would never have found otherwise.",
          { contact: "If you are a publisher and would like to discuss how IslandsDaily features your content, we welcome the conversation. Please reach out to us at " },
        ],
      },
      {
        h: "Built in Aruba",
        paras: [
          "IslandsDaily was founded in Aruba in 2026. We are a Caribbean business, built by someone who lives here, for people who live here and visit here. We are proud to call the Dutch Caribbean home and committed to serving these islands for the long term.",
        ],
      },
      {
        h: "Contact Us",
        paras: [
          "For advertising inquiries, content partnerships, content removal requests, or general questions:",
        ],
        contact_block: true,
      },
    ],
    email_label: "Email",
    website_label: "Website",
  },
  nl: {
    sections: [
      {
        h: "Ons Verhaal",
        paras: [
          "IslandsDaily is ontstaan vanuit een eenvoudige observatie: de eilanden van het Nederlandse Caribisch gebied hebben levendige lokale nieuwsscènes — maar bijna niets daarvan is toegankelijk in het Engels.",
          "Lokale kranten op Aruba, Curaçao, Bonaire en Sint Maarten publiceren honderden verhalen per week in het Papiaments, Nederlands en Papiamentu. Toeristen arriveren op deze eilanden zonder enig idee van wat er om hen heen gebeurt. Expats en langdurige bewoners puzzelen informatie bij elkaar van meerdere bronnen in talen die ze misschien niet volledig begrijpen. Zelfs Engelstalige locals vinden het moeilijk om nieuws van alle vier de eilanden op één plek te volgen.",
          "IslandsDaily is opgericht om dit op te lossen. Wij brengen koppen samen van de toonaangevende lokale nieuwsbronnen van alle vier de eilanden, vertalen en vatten ze samen in duidelijk Engels met behulp van AI, en presenteren alles op één overzichtelijk, gemakkelijk leesbaar platform — elke 15 minuten bijgewerkt.",
        ],
      },
      {
        h: "Wat Wij Doen",
        paras: [
          "Elke 15 minuten verzamelt IslandsDaily automatisch de laatste koppen van vertrouwde lokale nieuwsbronnen op Aruba, Curaçao, Bonaire en Sint Maarten. Ons AI-redactioneel systeem leest elk artikel — in welke taal het ook origineel is geschreven — en produceert een duidelijke, nauwkeurige Engelse samenvatting van 5 tot 7 zinnen die behandelt wie, wat, waar, wanneer en waarom het belangrijk is.",
          "Wij reproduceren nooit volledige artikelen. Wij geven altijd credit aan de originele bron. En wij geven lezers altijd een directe link om het volledige verhaal te lezen op de eigen website van de uitgever in de oorspronkelijke taal — moderne browsers bieden vertaling aan met één tik als de lezer dat wenst.",
        ],
      },
      {
        h: "Onze Missie",
        paras: [
          "De verhalen van het Nederlandse Caribisch gebied toegankelijk maken voor iedereen — ongeacht welke taal zij spreken.",
          "Wij geloven dat lokaal nieuws belangrijk is. Wij geloven dat de verhalen die plaatsvinden in Oranjestad, Willemstad, Kralendijk en Philipsburg verdienen te worden gehoord buiten de eilanden. En wij geloven dat taal nooit een barrière mag zijn om geïnformeerd te blijven over de plek waar je woont, werkt of op bezoek bent.",
        ],
      },
      {
        h: "Onze Relatie Met Lokale Uitgevers",
        paras: [
          "Wij hebben groot respect voor de journalisten en uitgevers die het harde werk doen van het rapporteren van lokaal Caribisch nieuws elke dag. IslandsDaily bestaat dankzij hun werk.",
          "Elk artikel dat wij uitlichten crediteert de originele uitgever en linkt rechtstreeks terug naar hun website. Wij sturen verkeer naar lokale nieuwsbronnen, niet weg ervan.",
          { contact: "Als u een uitgever bent en wilt bespreken hoe IslandsDaily uw inhoud weergeeft, verwelkomen wij het gesprek. Neem contact met ons op via " },
        ],
      },
      {
        h: "Gebouwd op Aruba",
        paras: [
          "IslandsDaily is opgericht op Aruba in 2026. Wij zijn een Caribisch bedrijf, gebouwd door iemand die hier woont, voor mensen die hier wonen en op bezoek komen.",
        ],
      },
      {
        h: "Contact",
        paras: [],
        contact_block: true,
      },
    ],
    email_label: "E-mail",
    website_label: "Website",
  },
  es: {
    sections: [
      {
        h: "Nuestra Historia",
        paras: [
          "IslandsDaily nació de una observación simple: las islas del Caribe neerlandés tienen escenas de noticias locales vibrantes y activas — pero casi nada de eso es accesible en inglés.",
          "Los periódicos locales en Aruba, Curaçao, Bonaire y Sint Maarten publican cientos de historias cada semana en papiamento, neerlandés y papiamentu. Los turistas llegan a estas islas sin saber qué está pasando a su alrededor. Los expatriados y residentes de larga data reúnen información de múltiples fuentes en idiomas que quizás no comprenden completamente. Incluso los locales de habla inglesa encuentran difícil seguir las noticias de las cuatro islas en un solo lugar.",
          "IslandsDaily fue creado para resolver eso. Reunimos titulares de las principales fuentes de noticias locales de las cuatro islas, los traducimos y resumimos en inglés claro usando IA, y presentamos todo en una plataforma limpia y fácil de leer — actualizada cada 15 minutos.",
        ],
      },
      {
        h: "Lo Que Hacemos",
        paras: [
          "Cada 15 minutos, IslandsDaily recopila automáticamente los últimos titulares de fuentes de noticias locales de confianza en Aruba, Curaçao, Bonaire y Sint Maarten. Nuestro sistema editorial de IA lee cada artículo — en cualquier idioma en que fue escrito originalmente — y produce un resumen en inglés claro y preciso de 5 a 7 oraciones que cubre quién, qué, dónde, cuándo y por qué importa.",
          "Nunca reproducimos artículos completos. Siempre damos crédito a la fuente original. Y siempre damos a los lectores un enlace directo para leer la historia completa en el sitio web del editor en su idioma original — los navegadores modernos ofrecen traducción con un toque si el lector lo desea.",
        ],
      },
      {
        h: "Nuestra Misión",
        paras: [
          "Hacer que las historias del Caribe neerlandés sean accesibles para todos — independientemente del idioma que hablen.",
          "Creemos que las noticias locales importan. Creemos que las historias que ocurren en Oranjestad, Willemstad, Kralendijk y Philipsburg merecen ser escuchadas más allá de las islas. Y creemos que el idioma nunca debe ser una barrera para mantenerse informado sobre el lugar donde vives, trabajas o visitas.",
        ],
      },
      {
        h: "Nuestra Relación Con Los Editores Locales",
        paras: [
          "Tenemos un profundo respeto por los periodistas y editores que hacen el arduo trabajo de reportar noticias locales del Caribe cada día. IslandsDaily existe gracias a su trabajo.",
          "Cada artículo que destacamos da crédito al editor original y enlaza directamente de vuelta a su sitio web. Dirigimos tráfico hacia las fuentes de noticias locales, no alejándonos de ellas.",
          { contact: "Si usted es un editor y desea hablar sobre cómo IslandsDaily presenta su contenido, le damos la bienvenida a la conversación. Contáctenos en " },
        ],
      },
      {
        h: "Construido en Aruba",
        paras: [
          "IslandsDaily fue fundado en Aruba en 2026. Somos una empresa caribeña, construida por alguien que vive aquí, para personas que viven aquí y nos visitan.",
        ],
      },
      {
        h: "Contacto",
        paras: [],
        contact_block: true,
      },
    ],
    email_label: "Correo electrónico",
    website_label: "Sitio web",
  },
};

const MailLink = () => (
  <a
    href={`mailto:${CONTACT}`}
    className="text-[#0AADA0] hover:underline"
    data-testid="about-contact-link"
  >
    {CONTACT}
  </a>
);

export default function AboutPage() {
  // __TOGGLE_HOOK__: self-contained reactive lang state for the mini-header toggle.
  const [__toggleLang, __setToggleLang] = useReactiveLang();

  const [lang, setLang] = useState("en");
  const copy = COPY[lang];

  useEffect(() => {
    document.title = "About — IslandsDaily";
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="about-page">
      {/* Slim back/home bar — matches Terms */}
      <div className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-[#EEEEEE]">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-[13px] text-[#6B7C82] hover:text-[#1A2E35]"
            data-testid="about-back-btn"
          >
            <ArrowLeft size={15} /> Back
          </Link>
          <Link
            to="/"
            className="ml-auto font-serif-display text-[18px] leading-none"
          >
            <span className="text-[#1A1A1A]">Islands</span>
            <span className="text-[#0AADA0]">Daily</span>
          </Link>
          {/* __LANG_TOGGLE__ */}
          <LanguageToggle lang={__toggleLang} onChange={__setToggleLang} />
        </div>
      </div>

      {/* Hero — always English (brand name) */}
      <div
        className="w-full flex flex-col items-center justify-center text-center px-4"
        style={{ background: "#0AADA0", height: 200 }}
        data-testid="about-hero"
      >
        <h1 className="font-serif-display text-[40px] leading-none text-white">
          IslandsDaily
        </h1>
        <p className="text-[14px] text-white/70 mt-3">
          Aruba · Curaçao · Bonaire · St. Maarten
        </p>
      </div>

      <article
        className="mx-auto px-4 sm:px-6 py-10 sm:py-14"
        style={{ maxWidth: "800px" }}
      >
        {/* Language tabs */}
        <div
          className="flex items-center justify-center gap-2 mb-12"
          role="tablist"
          data-testid="about-lang-tabs"
        >
          {LANGS.map((l) => (
            <button
              key={l.code}
              role="tab"
              aria-selected={lang === l.code}
              onClick={() => setLang(l.code)}
              className={`px-4 py-1.5 rounded-full text-[13px] transition-colors ${
                lang === l.code
                  ? "bg-[#0AADA0] text-white"
                  : "bg-[#F5F5F5] text-[#6B7C82] hover:bg-[#EEEEEE]"
              }`}
              style={{ fontFamily: "'DM Sans', system-ui, sans-serif" }}
              data-testid={`about-lang-${l.code}`}
            >
              {l.label}
            </button>
          ))}
        </div>

        {/* Sections */}
        <div
          className="space-y-10 text-[16px] text-[#1A2E35]"
          style={{
            lineHeight: 1.8,
            fontFamily: "'DM Sans', system-ui, sans-serif",
          }}
          data-testid={`about-content-${lang}`}
        >
          {copy.sections.map((s, i) => (
            <section key={i} data-testid={`about-section-${i}`}>
              <h2 className="font-serif-display text-[24px] text-[#1A2E35] mb-4">
                {s.h}
              </h2>
              {(s.paras || []).map((p, j) => {
                if (typeof p === "string") {
                  return (
                    <p key={j} className="mb-4 last:mb-0">
                      {p}
                    </p>
                  );
                }
                if (p.contact) {
                  return (
                    <p key={j} className="mb-4 last:mb-0">
                      {p.contact}
                      <MailLink />.
                    </p>
                  );
                }
                return null;
              })}
              {s.contact_block && (
                <div className="mt-3 space-y-1">
                  <div>
                    {copy.email_label}: <MailLink />
                  </div>
                  <div>
                    {copy.website_label}:{" "}
                    <a
                      href="https://islandsdaily.com"
                      className="text-[#0AADA0] hover:underline"
                    >
                      islandsdaily.com
                    </a>
                  </div>
                </div>
              )}
            </section>
          ))}
        </div>

        {/* Final flag row — grounds the page in the islands */}
        <div
          className="mt-16 flex items-center justify-center gap-6"
          data-testid="about-flag-row"
          aria-label="Aruba, Curaçao, Bonaire and St. Maarten"
        >
          <span className="h-px flex-1 bg-[#EEEEEE]" />
          <span className="text-[28px] leading-none">🇦🇼</span>
          <span className="text-[#EEEEEE]">·</span>
          <span className="text-[28px] leading-none">🇨🇼</span>
          <span className="text-[#EEEEEE]">·</span>
          <span className="text-[28px] leading-none">🇧🇶</span>
          <span className="text-[#EEEEEE]">·</span>
          <span className="text-[28px] leading-none">🇸🇽</span>
          <span className="h-px flex-1 bg-[#EEEEEE]" />
        </div>
      </article>

      <Footer />
    </div>
  );
}
